import PAButton from "component/design-system/buttons/PAButton";
import Text, { TEXT_TYPE } from "component/design-system/typography/Text";
import LandingPageBanner from "component/landing-page/LandingPageBanner";
import { RootState } from "interfaces/generics.type";
import TireSizeCalculator from "modules/modals/tire-size-calculator/TireSizeCalculator";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import { useState, useEffect, memo } from "react";
import { useSelector } from "react-redux";
import TiresAPI, { ITiresSizes } from "../../../api/tires.api";
import CategorySection from "../../../component/design-system/category-selection/CategorySection";
import DocumentMeta from "../../../component/design-system/DocumentMeta";
import PADescription from "../../../component/design-system/pa-description/PADescription";
import PageContainer from "../../components/PageContainer";
import DynamicCategoryViewItem from "./DynamicCategoryViewItem";
import TireAndWheelSizesButton from "./TireAndWheelSizesButton";
import TireSizePreview from "./TireSizePreview";
import { getRelativeImagePath } from "../../../util/helper/getRelativeImagePath";

const meta = {
  title: "Tire Size Calculator",
  description: "Here's how you can calculate your tire size to get the right size tire for your wheels",
  robots: "all",
  keywords: ["tire size calculator, tire calculator, calculate tire size, tire size, size of tires, tyre calculator, tyre size calculator, how to calculate tire size, right tire for my wheels, wheel and tire size calculator"],
};

function renderViewAllTires({
  size,
}: any) {
  return (
    <PAButton
      type="button"
      className="m-t-30 m-b-20"
      style={{ width: 250 }}
    >
      VIEW ALL
      {" "}
      {size}
      {" "}
      TIRES
    </PAButton>
  );
}

function TireCalculator(props: ITiresSizes) {
  const [tireSizes, setTireSizes] = useState(props.tireSizes);
  const [size, setSize] = useState("");
  const [calculatedSize, setCalculatedSize] = useState(null);
  const [showSizeDistribution, setSizeDistributions] = useState(false);

  const selectedVehicle = useSelector((state: RootState) => state.garage.selectedVehicle);
  const { t } = useTranslation("static-pages");

  const categoryObj = {
    tier0: "wheels & tires",
    tier1: "Tires",
    tier2: "Tires",
    tier3: "Tires",
  };
  const url = `/tires/${size.replace("/", "-")}`;

  const descriptionText = (
    <p className="text-justify">
      From your speedometer to ABS, many systems in the vehicle rely on the correct tire size for optimal performance. The right tire and wheel size influences your driving dynamics and is an important factor in deciding your suspension geometry, transmission final gear ratio, stability and traction control systems which ultimately influence safety, comfort and handling. Altering the tire size too much can affect your ability to accelerate or cause hindrances in the wheel well. So it is important to stick to the manufacturer's recommended tire and wheel size for the best performance. Although upsizing and downsizing your tire size is possible to a certain extent without impacting these systems and that is why a tire size calculator is an important tool. The tire size calculator can allow you to input your original or stock tire specifications in order to get equally matched tires and wheel combinations without affecting overall tire diameter.

      <br />

      <br />
      The tire size calculator can help you determine the diameter, width and sidewall height of your current tire. This information is important as it can help you find new tires that can fit your current wheel size. An important rule to remember when buying new tires or wheels is that the overall diameter of the wheel and tire assembly should always remain the same as factory specifications or as close to it as possible. This is because the overall tire diameter is used to determine the revolutions per kilometer of the tire which is an important factor influencing the speedometer and odometer reading.
      The right tire size also affects the traction and stability control systems in the vehicle like the ABS. However, this depends on the differences in tire sizes on all four wheels. Meaning if all four wheels are upsized or downsized equally, then the traction control system is not affected. If only one wheel or set of wheels (front or rear axle only) are upsized or downsized, then the traction control system will not function correctly. This is because the ABS system works by monitoring the speeds of all wheels when braking. If all four wheels are larger or smaller than stock wheels, the ABS will still function correctly since the diameter of all wheels is equal. If one or more tire sizes don't match, the ABS will miscalculate the braking force and duration which will affect its performance.
      When upgrading from stock tires and wheels, a common practice is to increase the width of the tire and wheel diameter, while reducing the sidewall height. This is done mainly for two reasons. The most common reason for doing this is to enhance the aesthetics of the vehicle. A car with slim (shorter sidewall height) tires and large wheels tend to look aggressive which is why they are the most common visual upgrade on cars. Wider tires also add a similar appearance. Secondly, this can also increase the handling dynamics of the vehicle, however, this entirely depends on the modifications made. Getting larger diameter alloy wheels than stock steel ones can increase the stiffness of the wheels as well as reduce unsprung mass which ultimately enhances the handling characteristics of the car. Shorter sidewalls and wider tires also improve the handling dynamics by providing a firmer ride.
      However, there is a certain limit to the gains you can get by doing these modifications and also depends on the products used. Low quality or poorly built alloy wheels will not reduce the weight and may even be heavier than the stock wheels. Getting extremely wide tires can cause hydroplaning and the extra weight of the rubber can negatively impact the suspension system. Slim sidewalls are known to increase NVH (noise, vibration and harshness) due to the reduction in rubber. For a deeper understanding of the
      {" "}

      <Link href="/how-to-calculate-tire-size">tire size calculations and their immense importance</Link>
      {" "}
      refer to this guide before you make your tire and wheel buying decision.
    </p>
  );

  useEffect(() => {
    TiresAPI.getAllTireSizes(selectedVehicle)
      .then(({ data }) => {
        const tireSizeResponse = data.tireSizes.map((tireSize) => ({
          size: tireSize,
        }));
        setTireSizes(tireSizeResponse);
      });
  }, [selectedVehicle]);

  function handleSizeSelect(selectedSize: any) {
    setSize(selectedSize);
  }

  function calculateSize() {
    setCalculatedSize(size);
    setSizeDistributions(true);
  }

  return (
    <PageContainer>

      <DocumentMeta {...meta} />

      <LandingPageBanner
        className="m-t-10"
        style={{ height: "auto" }}
        bannerImages={{
          desktopImage: getRelativeImagePath("/images/tires/banners/tire-calculator-banner-desktop-min.jpg"),
          mobileImage: getRelativeImagePath("/images/tires/banners/tire-calculator-banner-mobile-min.jpg"),
        }}
        // optimisedSrc="https://partsavatar.ca/images/tires/tires-size-calculator-min.jpg"
        alt="tire calculator"
      />

      <Text size={1} bold className="text-center">
        {t("Tire Size Calculator")}
      </Text>

      <Text
        size={3}
        bold
        className="text-left p-t-20"
        type={TEXT_TYPE.secondary}
      >
        {t("tire_calculator_secondary")}
      </Text>

      <div className="p-t-20 d-flex row flex-wrap p-b-20">

        <div className="col-md-4 col-xs-12">

          <Text size={3} bold className="text-left p-t-20">
            {t("Select Tire size")}
          </Text>

          <div>

            <TireSizeCalculator handleSizeSelect={handleSizeSelect} calculateSize={calculateSize} />

            <div className="text-center p-t-20">

              <img
                src={getRelativeImagePath("/images/tires/tire-size-info-min.png")}
                style={{ width: "50%", minWidth: "calc(20 * var(--safari-root-font, 10px))" }}
                alt="size-indicator"
              />
            </div>
          </div>
        </div>

        <div className="col-md-8 col-xs-12 align-items-center d-flex justify-content-center flex-column">
          {!showSizeDistribution && (
            <img
              src={getRelativeImagePath("/images/tires/tire-cal.png")}
              style={{ width: "52%", minWidth: "calc(33 * var(--safari-root-font, 10px))" }}
              alt="tire-calculator"
            />
          )}
          {size && showSizeDistribution && (
            <>

              <TireSizePreview {...{ calculatedSize }} />

              <DynamicCategoryViewItem
                selectedVehicle={selectedVehicle}
                size={size}
                categoryObj={categoryObj}
                defaultFilters={{
                  sizes: calculatedSize,
                }}
                url={url}
              >
                {renderViewAllTires({ size })}
              </DynamicCategoryViewItem>
            </>
          )}
        </div>
      </div>

      <CategorySection {...{
        title: `${t("Other Popular Sizes")}:`,
        categoryUrls: tireSizes,
      }}
      >

        <TireAndWheelSizesButton selectedVehicle={selectedVehicle} />
      </CategorySection>

      <PADescription>

        <Text
          size={4}
          className="text-left"
          type={TEXT_TYPE.secondary}
        >
          {descriptionText}
        </Text>
      </PADescription>
    </PageContainer>
  );
}

export default memo(TireCalculator);
